import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Grid, Button, Tooltip } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle as Required } from '@fortawesome/pro-light-svg-icons';
import { _NkInput as NkInput, NkSelect } from '@nordkap/nordkap-ui';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import appInsights from '../../infrastructure/appInsights';
import { combineClasses } from '../../utils/styling';
import { convertToPositionType } from '../../utils/route';
import { TemplateEditor } from '../../components/templateEditor';
import {
  ACCT_ADMIN_PERMISSION,
  ACCT_ASSIGN_PERMISSION,
  emptyGuid,
} from '../../utils/constants';
import api from '../../infrastructure/api/api';
import { useStyles } from './styles';
import PositionPreview from './PositionPreview';
import { extractHeader, extractRows } from './extractData';
import authCookie from '../../infrastructure/api/auth-cookie';
import { alphabetically } from '../../utils/array';

const Position = () => {
  const [positionFields, setPositionFields] = useState({
    customFields: [],
    positionTemplateId: '',
  });
  const [positionTemplateOptions, setPositionTemplateOptions] = useState([]);
  const [actualPositionType, setActualPositionType] = useState('');
  const [dialogObject, setDialogObject] = useState({ isOpen: false });
  const [header, setHeader] = useState([]);
  const [rows, setRows] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [dirtyState, setDirtyState] = useState(false);
  const [routeUnavailable, setRouteUnavailable] = useState(false);

  const hasAssignPermission = authCookie.isAllowed(ACCT_ASSIGN_PERMISSION);
  const hasAdminPermission = authCookie.isAllowed(ACCT_ADMIN_PERMISSION);

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { positionType, positionId } = useParams();
  const classes = useStyles();

  const makeDirty = useCallback(() => {
    if (!dirtyState) {
      setDirtyState(true);
    }
  }, [dirtyState]);

  const createPositionFields = () => ({
    positionId,
    type: actualPositionType,
    customFields: positionFields.customFields.map(cf => ({
      ...cf,
      value: cf.value ? cf.value.trim() : cf.value,
    })),
    positionTemplateId: positionFields.positionTemplateId,
  });

  const updateTemplatePreview = useCallback(
    updatedPositionFields => {
      if (
        positionId !== '' &&
        updatedPositionFields.positionTemplateId !== '' &&
        !routeUnavailable
      ) {
        api
          .post(
            `positionFields/Preview?positionId=${positionId}&positionTemplateId=${
              updatedPositionFields.positionTemplateId
            }&positionType=${convertToPositionType(positionType)}`,
            enqueueSnackbar,
            t
          )
          .then(result => {
            if (result.success) {
              setHeader(extractHeader(result.data, t));
              setRows(extractRows(result.data, t));
              setPreviewVisible(true);
            } else {
              if (result.statusCode === 405) {
                setRouteUnavailable(true);
              }
              setPreviewVisible(false);
            }
          });
      } else {
        setPreviewVisible(false);
      }
    },
    [routeUnavailable, positionId, positionType, enqueueSnackbar, t]
  );

  const savePositionFields = () => {
    const newPositionFields = createPositionFields();
    api.post('positionFields', newPositionFields, enqueueSnackbar, t);
    updateTemplatePreview(newPositionFields);
    setDirtyState(false);
  };

  const changeCustomField = (value, fieldId) => {
    const field = positionFields.customFields.find(
      f => f.customFieldId === fieldId
    );
    field.value = value;
    setPositionFields({ ...positionFields });
    makeDirty();
  };

  const noneOption = useMemo(
    () => ({
      value: '',
      title: <em>{t('positionPage.notSelectedTemplate')}</em>,
    }),
    [t]
  );

  useEffect(() => {
    appInsights.trackPageView();

    const convertedPositionType = convertToPositionType(positionType);
    setActualPositionType(convertedPositionType);

    Promise.all([
      api.get(`positionFields?id=${positionId}`, enqueueSnackbar, t),
      api.get(
        `positionTemplates/getAll?positionType=${convertedPositionType}`,
        enqueueSnackbar,
        t
      ),
    ]).then(results => {
      const positionFieldsResult = results[0];
      const positionTemplatesResult = results[1];

      if (positionFieldsResult && positionTemplatesResult) {
        const templatesToShow = positionTemplatesResult.filter(
          pt => !pt.archived
        );

        if (
          positionFieldsResult.positionTemplateId &&
          !templatesToShow.some(
            pt =>
              pt.positionTemplateId === positionFieldsResult.positionTemplateId
          )
        ) {
          const selectedTemplate = positionTemplatesResult.find(
            pt =>
              pt.positionTemplateId === positionFieldsResult.positionTemplateId
          );

          if (selectedTemplate) {
            selectedTemplate.name = `[${t('positionTemplate.archived')}] ${
              selectedTemplate.name
            }`;
            templatesToShow.push(selectedTemplate);
          }
        }

        setPositionTemplateOptions(
          [noneOption].concat(
            templatesToShow
              .map(pt => ({ value: pt.positionTemplateId, title: pt.name }))
              .sort(alphabetically('title'))
          )
        );
        setPositionFields(positionFieldsResult);
        updateTemplatePreview(positionFieldsResult);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionId, positionType]);

  const addPositionTemplateOption = useCallback(
    templateListItem => {
      const newListItem = {
        value: templateListItem.positionTemplateId,
        title: templateListItem.name,
      };
      const existingListItems = positionTemplateOptions.filter(
        pto => pto.value !== ''
      );
      const newTemplateListOptions = [newListItem].concat(
        existingListItems.sort(alphabetically('title'))
      );
      setPositionTemplateOptions([noneOption].concat(newTemplateListOptions));
    },
    [noneOption, positionTemplateOptions]
  );

  const editPositionTemplateOption = useCallback(
    (existingTemplateOption, savedTemplate) => {
      if (savedTemplate.archived) {
        existingTemplateOption.title = `[${t('positionTemplate.archived')}] ${
          savedTemplate.name
        }`;
      } else {
        existingTemplateOption.title = savedTemplate.name;
      }
      const existingListItems = positionTemplateOptions.filter(
        pto => pto.value !== ''
      );
      setPositionTemplateOptions(
        [noneOption].concat(existingListItems.sort(alphabetically('title')))
      );
    },
    [noneOption, positionTemplateOptions, t]
  );

  const templateSaved = useCallback(
    savedTemplate => {
      const existingTemplateOption = positionTemplateOptions.find(
        pto => pto.value === savedTemplate.positionTemplateId
      );
      if (existingTemplateOption) {
        editPositionTemplateOption(existingTemplateOption, savedTemplate);
      } else {
        addPositionTemplateOption(savedTemplate);
      }
      positionFields.positionTemplateId = savedTemplate.positionTemplateId;
      setPositionFields({ ...positionFields });
      setDialogObject({ isOpen: false });
      updateTemplatePreview(positionFields);
      makeDirty();
    },
    [
      addPositionTemplateOption,
      editPositionTemplateOption,
      positionFields,
      positionTemplateOptions,
      updateTemplatePreview,
      makeDirty,
    ]
  );

  const createNewTemplate = useCallback(() => {
    setDialogObject({
      positionTemplateId: emptyGuid,
      isOpen: true,
      templateSaved,
    });
  }, [templateSaved]);

  const editTemplate = useCallback(() => {
    setDialogObject({
      positionTemplateId: positionFields.positionTemplateId,
      isOpen: true,
      templateSaved,
    });
  }, [positionFields.positionTemplateId, templateSaved]);

  const handleTemplateChange = useCallback(
    event => {
      positionFields.positionTemplateId = event.target.value;
      setPositionFields({ ...positionFields });
      updateTemplatePreview(positionFields);
      makeDirty();
    },
    [positionFields, updateTemplatePreview, makeDirty]
  );

  const getFillerWidth = useCallback(
    () => (4 - (positionFields.customFields.length % 4)) * 3,
    [positionFields.customFields.length]
  );

  const memoizedPreview = useMemo(
    () =>
      previewVisible && (
        <>
          <Grid item xs={12}>
            <h1 className={classes.Title}>{t('positionPage.preview')}</h1>
          </Grid>
          <Grid
            id="preview-position-template"
            item
            container
            xs={12}
            justifyContent="flex-end"
          >
            <PositionPreview tableHeader={header} tableRows={rows} />
          </Grid>
        </>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewVisible, rows]
  );

  return (
    <div>
      {hasAdminPermission && (
        <TemplateEditor
          positionType={actualPositionType}
          dialogObject={dialogObject}
          closeDialog={() => setDialogObject({ isOpen: false })}
        />
      )}

      <div>
        <Grid container>
          <Grid
            item
            container
            xs={12}
            justifyContent="center"
            alignItems="stretch"
            spacing={3}
          >
            {positionFields.customFields.length !== 0 && (
              <>
                <Grid item xs={12}>
                  <h1 className={classes.Title}>{t('positionFields.title')}</h1>
                </Grid>
                {positionFields.customFields.map(customField => (
                  <Grid item xs={3} key={customField.customFieldId}>
                    <div className={classes.Field}>
                      <label
                        className={combineClasses([
                          classes.Bold,
                          classes.UpperCase,
                        ])}
                        htmlFor={customField.name}
                      >
                        {customField.name}
                      </label>
                      <NkInput
                        name={customField.name}
                        type="text"
                        variant="outlined"
                        value={customField.value}
                        onChange={event =>
                          changeCustomField(
                            event.target.value,
                            customField.customFieldId
                          )
                        }
                      />
                    </div>
                  </Grid>
                ))}
                <Grid item xs={getFillerWidth()} />
              </>
            )}
            <Grid item xs={12}>
              <h1 className={classes.Title}>{t('positionPage.rules')}</h1>
            </Grid>
            <Grid item xs={12} container>
              <Grid item xs={8} container alignItems="flex-end">
                <div className={classes.TemplateDropdown}>
                  <label
                    className={combineClasses([
                      classes.Bold,
                      classes.UpperCase,
                    ])}
                  >
                    {t('positionPage.template')}
                  </label>
                  <NkSelect
                    variant="outlined"
                    name={t('positionPage.template')}
                    value={positionFields.positionTemplateId}
                    onChange={handleTemplateChange}
                    items={positionTemplateOptions}
                    itemPropName="value"
                    itemTitlePropName="title"
                    wrapper={false}
                  />
                </div>
                {hasAdminPermission && (
                  <>
                    <Button
                      id="edit-position-template"
                      variant="outlined"
                      color="primary"
                      title={t('positionPage.editTemplate')}
                      onClick={editTemplate}
                      className={classes.TemplateButton}
                      disabled={
                        !positionFields.positionTemplateId ||
                        positionFields.positionTemplateId === ''
                      }
                    >
                      <EditIcon />
                    </Button>
                    <Button
                      id="create-position-template"
                      variant="outlined"
                      color="primary"
                      title={t('positionPage.createTemplate')}
                      onClick={createNewTemplate}
                      className={classes.TemplateButton}
                    >
                      <AddIcon />
                    </Button>
                  </>
                )}
              </Grid>
              <Grid
                item
                xs={4}
                container
                alignItems="flex-end"
                justifyContent="flex-end"
              >
                {dirtyState && (
                  <Tooltip
                    title={t('positionPage.unsavedChangesWarning')}
                    placement="top"
                    arrow
                  >
                    <div className={classes.iconWrapper}>
                      <FontAwesomeIcon
                        icon={Required}
                        className={`${classes.endAdornmentIcon} ${classes.errorIcon}`}
                      />
                    </div>
                  </Tooltip>
                )}
                {hasAssignPermission && (
                  <Button
                    id="save-position-fields"
                    variant="contained"
                    color="primary"
                    title={t('global.buttons.saveAll')}
                    onClick={savePositionFields}
                    className={classes.TemplateButton}
                  >
                    {t('global.buttons.saveAll')}
                  </Button>
                )}
              </Grid>
            </Grid>
            {memoizedPreview}
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default Position;
